import { Controller } from "@hotwired/stimulus"

// Press-and-hold for the timesheet +/- steppers. A tap still steps on its click, as before;
// a press held past HOLD_DELAY_MS starts stepping by itself — slowly at first, then faster,
// so a big jump (07:00 → 17:00) does not take a hundred taps — and the click that ends the
// hold adds nothing. Nothing steps on the press itself: a finger that lands on the button
// while scrolling the form is cancelled (pointercancel) before it changes a value.
//
// `step` returns false once it has hit its limit, which ends the repeat instead of
// hammering a clamped value.
export const HOLD_DELAY_MS = 400
export const REPEAT_INTERVAL_MS = 120
export const FAST_INTERVAL_MS = 50
export const FAST_AFTER_MS = 1500

export default class HoldRepeat {
  held = false
  pressing = false

  // `held` belongs to this press alone. A hold that ended without a click (a touch
  // long-press often fires none) must not swallow the next tap.
  press(step) {
    this.cancel()
    this.held = false
    this.pressing = true
    // A pointerup that lands while the window is in the background never reaches the
    // button; without this the value would keep running.
    window.addEventListener("blur", this.cancel)

    const startedAt = Date.now()
    const tick = () => {
      this.held = true
      if (!step()) {
        this.stopTimer()
        return
      }

      const interval =
        Date.now() - startedAt >= FAST_AFTER_MS ? FAST_INTERVAL_MS : REPEAT_INTERVAL_MS
      this.timer = setTimeout(tick, interval)
    }
    this.timer = setTimeout(tick, HOLD_DELAY_MS)
  }

  // pointerup: stop repeating, but keep `held` so the click that follows is swallowed.
  release = () => {
    this.stopTimer()
    this.pressing = false
    window.removeEventListener("blur", this.cancel)
  }

  // The press ended early (pointer left, scroll took over, window lost focus). `held`
  // stays: a mouse dragged off and back onto the button still clicks on release, and
  // that click ends the hold too.
  cancel = () => {
    if (!this.pressing) return

    this.release()
  }

  // True when this click ends a hold (it must not step again). Keyboard activation
  // (Enter/Space) arrives with detail 0 and always steps.
  consumeHold(event) {
    const held = this.held && event.detail !== 0
    this.held = false
    return held
  }

  stopTimer() {
    clearTimeout(this.timer)
    this.timer = null
  }
}

// The press/release/cancel/click wiring both steppers share. A subclass implements
// `step(direction)`, returning false once the value stops moving; a subclass with its
// own connect/disconnect calls super.
export class HoldRepeatController extends Controller {
  connect() {
    this.holdRepeat = new HoldRepeat()
  }

  disconnect() {
    this.holdRepeat.cancel()
  }

  // A tap steps on click; a held press repeats (see HoldRepeat).
  press(event) {
    if (event.button !== 0 || event.currentTarget.disabled) return

    const direction = event.params.direction
    this.holdRepeat.press(() => this.step(direction))
  }

  release() {
    this.holdRepeat.release()
  }

  cancel() {
    this.holdRepeat.cancel()
  }

  click(event) {
    if (this.holdRepeat.consumeHold(event)) return

    this.step(event.params.direction)
  }

  suppressMenu(event) {
    event.preventDefault()
  }
}
